import * as React from 'react';
import { Track } from 'livekit-client';
import { useLocalParticipant, usePersistentUserChoices, useRoomContext, useTrackToggle, } from '@livekit/components-react';
import { usePublishPermissions } from './use-publish-permissions';
export function useAgentControlBar(props = {}) {
    var _a, _b, _c, _d;
    const { controls, saveUserChoices = true } = props;
    const visibleControls = Object.assign({ leave: true }, controls);
    const { microphoneTrack, localParticipant } = useLocalParticipant();
    const publishPermissions = usePublishPermissions();
    const room = useRoomContext();
    const microphoneToggle = useTrackToggle({
        source: Track.Source.Microphone,
        onDeviceError: (error) => { var _a; return (_a = props.onDeviceError) === null || _a === void 0 ? void 0 : _a.call(props, { source: Track.Source.Microphone, error }); },
    });
    const cameraToggle = useTrackToggle({
        source: Track.Source.Camera,
        onDeviceError: (error) => { var _a; return (_a = props.onDeviceError) === null || _a === void 0 ? void 0 : _a.call(props, { source: Track.Source.Camera, error }); },
    });
    const screenShareToggle = useTrackToggle({
        source: Track.Source.ScreenShare,
        onDeviceError: (error) => { var _a; return (_a = props.onDeviceError) === null || _a === void 0 ? void 0 : _a.call(props, { source: Track.Source.ScreenShare, error }); },
    });
    const micTrackRef = React.useMemo(() => {
        return {
            participant: localParticipant,
            source: Track.Source.Microphone,
            publication: microphoneTrack,
        };
    }, [localParticipant, microphoneTrack]);
    (_a = visibleControls.microphone) !== null && _a !== void 0 ? _a : (visibleControls.microphone = publishPermissions.microphone);
    (_b = visibleControls.screenShare) !== null && _b !== void 0 ? _b : (visibleControls.screenShare = publishPermissions.screenShare);
    (_c = visibleControls.camera) !== null && _c !== void 0 ? _c : (visibleControls.camera = publishPermissions.camera);
    (_d = visibleControls.chat) !== null && _d !== void 0 ? _d : (visibleControls.chat = publishPermissions.data);
    const { saveAudioInputEnabled, saveAudioInputDeviceId, saveVideoInputEnabled, saveVideoInputDeviceId, } = usePersistentUserChoices({
        preventSave: !saveUserChoices,
    });
    const handleDisconnect = React.useCallback(() => {
        if (room) {
            room.disconnect();
        }
    }, [room]);
    const handleAudioDeviceChange = React.useCallback((deviceId) => {
        saveAudioInputDeviceId(deviceId !== null && deviceId !== void 0 ? deviceId : 'default');
    }, [saveAudioInputDeviceId]);
    const handleVideoDeviceChange = React.useCallback((deviceId) => {
        saveVideoInputDeviceId(deviceId !== null && deviceId !== void 0 ? deviceId : 'default');
    }, [saveVideoInputDeviceId]);
    const handleToggleCamera = React.useCallback(async (enabled) => {
        if (screenShareToggle.enabled) {
            screenShareToggle.toggle(false);
        }
        await cameraToggle.toggle(enabled);
        // persist video input enabled preference
        saveVideoInputEnabled(!cameraToggle.enabled);
    }, [cameraToggle.enabled, screenShareToggle.enabled]);
    const handleToggleMicrophone = React.useCallback(async (enabled) => {
        await microphoneToggle.toggle(enabled);
        // persist audio input enabled preference
        saveAudioInputEnabled(!microphoneToggle.enabled);
    }, [microphoneToggle.enabled]);
    const handleToggleScreenShare = React.useCallback(async (enabled) => {
        if (cameraToggle.enabled) {
            cameraToggle.toggle(false);
        }
        await screenShareToggle.toggle(enabled);
    }, [screenShareToggle.enabled, cameraToggle.enabled]);
    return {
        micTrackRef,
        visibleControls,
        cameraToggle: Object.assign(Object.assign({}, cameraToggle), { toggle: handleToggleCamera }),
        microphoneToggle: Object.assign(Object.assign({}, microphoneToggle), { toggle: handleToggleMicrophone }),
        screenShareToggle: Object.assign(Object.assign({}, screenShareToggle), { toggle: handleToggleScreenShare }),
        handleDisconnect,
        handleAudioDeviceChange,
        handleVideoDeviceChange,
    };
}
